import type { NotificationSeverity, TicketStatus } from '@nadoutong/contracts';
import { AlertTriangle, Info, LifeBuoy, OctagonAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTicketsList } from '../api/useTickets.ts';
import { formatTimestamp } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { listNotices, markAllRead } from '../notifications/store.ts';
import type { StoredNotice } from '../notifications/store.ts';
import {
  buildTicketTitleIndex,
  collectTicketNoticeIds,
  detectTicketNoticeStatus,
  isTicketNotice,
  isTicketReplyNotice,
  noticeSegmentsPlainText,
  promoteTicketTitleNotice,
  replaceTicketIdSegments,
} from '../notifications/ticketNotice.ts';
import type { NoticeTextSegment } from '../notifications/ticketNotice.ts';
import { useNotificationsSync, useStoredNotices } from '../notifications/useNotifications.ts';
import { EmptyState, PageHeader, Panel, cx } from '../ui/index.ts';

/**
 * `/notifications` — the ONLY place notification content renders (the
 * header bell is a dot + a link here, no popover; 2026-08-21 决定).
 *
 * Read state is local-only (no server ack this round): entering this
 * page marks everything read, which is what turns the bell's dot off.
 * The entries that were unread at that moment keep a visual "new"
 * marker for the remainder of the visit, so marking-read does not erase
 * the answer to "what changed since I last looked".
 *
 * Ticket-kind entries follow the desktop-finalized presentation: the
 * `tk_` id resolved through the tickets list becomes the emphasized
 * heading, the body keeps the remaining text (「状态变更为 X」 / a
 * quoted reply), status drives the icon color, and the row links to
 * `/tickets`. Recognition failures of any kind fall back to the raw
 * server copy. Fingerprints are compared elsewhere and never parsed
 * here.
 */

// Title lookup page size: the local store keeps at most 200 rows and
// the server clamps limit to 100; unresolved ids just stay verbatim.
const TICKET_TITLE_LOOKUP_LIMIT = 100;

const SEVERITY_ICON_CLASSES: Readonly<Record<NotificationSeverity, string>> = {
  info: 'bg-forest-tint text-forest',
  warning: 'bg-warn-tint text-warn',
  critical: 'bg-bad-tint text-bad',
};

const TICKET_STATUS_ICON_CLASSES: Readonly<Record<TicketStatus, string>> = {
  open: 'bg-warn-tint text-warn',
  processing: 'bg-rust-tint text-rust-text',
  resolved: 'bg-ok-tint text-forest-deep',
  closed: 'bg-well text-ink-low',
};

export function Notifications(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const scope = user?.orgId ?? null;
  useNotificationsSync();
  const notices = useStoredNotices(scope);

  /**
   * Fingerprints that were unread when the reader could see them.
   * Marking-read is destructive to exactly the information the page
   * needs to show ("these are new"), so it is captured first. The
   * effect re-runs when a background sync lands new unread entries
   * mid-visit; `markAllRead` no-ops (and emits nothing) once everything
   * is read, so the loop terminates.
   */
  const [freshFingerprints, setFreshFingerprints] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (scope === null) {
      return;
    }
    const unread = listNotices(scope).filter((notice) => !notice.read);
    if (unread.length === 0) {
      return;
    }
    setFreshFingerprints((previous) => {
      const next = new Set(previous);
      for (const notice of unread) {
        next.add(notice.fingerprint);
      }
      return next;
    });
    markAllRead(scope);
  }, [scope, notices]);

  // Ticket-title lookup for the id→title substitution; only issued when
  // some ticket notice actually mentions a tk_ id. A failed lookup
  // yields an empty index and the raw copy renders unchanged.
  const ticketIds = useMemo(() => collectTicketNoticeIds(notices), [notices]);
  const ticketsQ = useTicketsList(
    { status: 'all', limit: TICKET_TITLE_LOOKUP_LIMIT, offset: 0 },
    ticketIds.length > 0,
  );
  const ticketTitles = useMemo(
    () => buildTicketTitleIndex(ticketsQ.data?.tickets ?? []),
    [ticketsQ.data],
  );

  return (
    <section data-testid="notifications-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('notifications.title')}
        sub={t('notifications.subtitle')}
        testId="notifications-header"
      />
      <Panel caption={t('notifications.title')} testId="notifications-panel">
        {notices.length === 0 ? (
          <EmptyState
            testId="notifications-empty"
            title={t('notifications.empty.title')}
            hint={t('notifications.empty.hint')}
          />
        ) : (
          <div className="flex flex-col">
            {notices.map((notice, index) => (
              <NoticeRow
                key={notice.fingerprint}
                notice={notice}
                first={index === 0}
                fresh={freshFingerprints.has(notice.fingerprint)}
                ticketTitles={ticketTitles}
                onOpenTickets={() => {
                  void navigate('/tickets');
                }}
              />
            ))}
          </div>
        )}
      </Panel>
    </section>
  );
}

function NoticeRow({
  notice,
  first,
  fresh,
  ticketTitles,
  onOpenTickets,
}: {
  readonly notice: StoredNotice;
  readonly first: boolean;
  readonly fresh: boolean;
  readonly ticketTitles: ReadonlyMap<string, string>;
  readonly onOpenTickets: () => void;
}): JSX.Element {
  const t = useT();
  const isTicket = isTicketNotice(notice);
  // Status coloring reads the DISPLAY text only — the #116 fingerprint
  // is never parsed.
  const ticketStatus = isTicket
    ? detectTicketNoticeStatus(`${notice.title}\n${notice.description}`)
    : null;
  const promoted = isTicket
    ? promoteTicketTitleNotice(
        replaceTicketIdSegments(notice.title, ticketTitles),
        replaceTicketIdSegments(notice.description, ticketTitles),
      )
    : {
        title: [{ text: notice.title, emphasized: false }],
        description: [{ text: notice.description, emphasized: false }],
        promoted: false,
      };
  const titleSegments: readonly NoticeTextSegment[] = promoted.title;
  // Reply-shaped notices get a localized quote prefix once the title
  // was promoted, so the body reads as a reply, not a status change.
  const descriptionSegments: readonly NoticeTextSegment[] =
    promoted.promoted && isTicketReplyNotice(notice.title)
      ? [{ text: t('notifications.reply_prefix'), emphasized: false }, ...promoted.description]
      : promoted.description;

  const body = (
    <>
      <span
        aria-hidden="true"
        className={cx(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          ticketStatus !== null
            ? TICKET_STATUS_ICON_CLASSES[ticketStatus]
            : SEVERITY_ICON_CLASSES[notice.severity],
        )}
        data-testid={`notifications-icon-${notice.id}`}
        data-severity={ticketStatus === null ? notice.severity : undefined}
        data-ticket-status={ticketStatus ?? undefined}
      >
        {ticketStatus !== null || isTicket ? (
          <LifeBuoy className="h-3.5 w-3.5" />
        ) : notice.severity === 'critical' ? (
          <OctagonAlert className="h-3.5 w-3.5" />
        ) : notice.severity === 'warning' ? (
          <AlertTriangle className="h-3.5 w-3.5" />
        ) : (
          <Info className="h-3.5 w-3.5" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-2">
          <span
            className="min-w-0 truncate text-body font-medium text-ink"
            title={noticeSegmentsPlainText(titleSegments)}
            data-testid={`notifications-title-${notice.id}`}
          >
            <NoticeText segments={titleSegments} />
          </span>
          <span className="shrink-0 font-mono text-mono-label tabular-nums text-ink-low">
            {formatTimestamp(notice.createdAt)}
          </span>
        </span>
        <span
          className="line-clamp-2 text-caption text-ink-mid"
          title={noticeSegmentsPlainText(descriptionSegments)}
          data-testid={`notifications-body-${notice.id}`}
        >
          <NoticeText segments={descriptionSegments} />
        </span>
      </span>
      {fresh ? (
        <span
          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-bad"
          data-testid={`notifications-fresh-${notice.id}`}
        >
          <span className="sr-only">{t('notifications.unread_marker')}</span>
        </span>
      ) : null}
    </>
  );

  const rowClass = cx(
    'flex w-full gap-3 px-5 py-3 text-left transition-colors duration-150',
    !first && 'border-t border-line-soft',
    fresh && 'bg-active/40',
  );

  if (isTicket) {
    return (
      <button
        type="button"
        aria-label={t('notifications.open_ticket_aria')}
        data-testid={`notifications-row-${notice.id}`}
        onClick={onOpenTickets}
        className={cx(
          rowClass,
          'hover:bg-active/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
        )}
      >
        {body}
      </button>
    );
  }
  return (
    <div data-testid={`notifications-row-${notice.id}`} className={rowClass}>
      {body}
    </div>
  );
}

/** Emphasized segments (substituted ticket titles) render bold; the rest verbatim. */
function NoticeText({
  segments,
}: {
  readonly segments: readonly NoticeTextSegment[];
}): JSX.Element {
  return (
    <>
      {segments.map((segment, index) =>
        segment.emphasized ? (
          <strong key={`${String(index)}-${segment.text}`} className="font-semibold text-ink">
            {segment.text}
          </strong>
        ) : (
          <span key={`${String(index)}-${segment.text}`}>{segment.text}</span>
        ),
      )}
    </>
  );
}
